import gsap, { Flip } from "@/lib/gsap";
import { useClickOutside } from "@/lib/hooks/useClickOutside";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import { updateManyProductFilterOptions } from "@/lib/redux/productFilters/productFiltersSlice";
import {
  productFilterOptionSelectors,
  productFilterSelectors,
} from "@/lib/redux/store";
import { useRouter } from "next/router";
import { useTranslation } from "@/lib/translations";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  Content,
  Control,
  Indicator,
  Menu,
  MenuItem,
} from "./styles";

type Props = PropsWithChildren<{
  catalogs?: {
    id: string;
    title: string;
    isCurrent: boolean;
  }[];
}>;

const Filter: React.FC<Props> = (props) => {
  const { children, catalogs } = props;
  const dispatch = useAppDispatch();

  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentCatalog = useMemo(
    () => catalogs?.find((catalog) => catalog.isCurrent),
    [catalogs]
  );

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  useClickOutside(containerRef, () => {
    if (menuIsOpen) setMenuIsOpen(false);
  });

  const router = useRouter();
  const { locale = "it" } = router;
  const DEFAULT_LABEL = useTranslation("productListing.allProducts", locale) || "Tutti i prodotti";
  useEffect(() => {
    const closeMenu = () => {
      setMenuIsOpen(false);
    };
    router.events.on("routeChangeComplete", closeMenu);
    return () => router.events.off("routeChangeComplete", closeMenu);
  }, [router.events]);

  useEffect(() => {
    const container = containerRef.current;
    const menu = menuRef.current;
    if (!menu || !container) return;

    const state = Flip.getState([menu, container]);
    if (menuIsOpen) {
      menu.style.height = "auto";
    } else {
      menu.style.height = "0px";
      menu.style.overflow = "hidden";
    }
    Flip.from(state, {
      duration: 0.3,
      onEnter: (elements) => {
        gsap.fromTo(elements, { opacity: 0 }, { opacity: 1 });
      },
      onLeave: (elements) =>
        gsap.fromTo(elements, { opacity: 1 }, { opacity: 0 }),
    });
  }, [menuIsOpen]);

  const filters = useAppSelector(productFilterSelectors.selectAll);

  const resetOtherFilters = useCallback(() => {
    filters.forEach((filter) => {
      if (filter.id !== "catalog") {
        const options = productFilterOptionSelectors.selectAll(filter);
        dispatch(
          updateManyProductFilterOptions({
            filterId: filter.id,
            updates: options.map((option) => ({
              id: option.value,
              changes: {
                selected: false,
              },
            })),
          })
        );
      }
    });
  }, [dispatch, filters]);

  const selectCatalog = useCallback(
    (id: string) => {
      if (catalogs) {
        dispatch(
          updateManyProductFilterOptions({
            filterId: "catalog",
            updates: catalogs.map((catalog) => ({
              id: catalog.id,
              changes: {
                selected: catalog.id === id,
              },
            })),
          })
        );
        resetOtherFilters();
      }
    },
    [dispatch, catalogs, resetOtherFilters]
  );

  const selectNone = useCallback(() => {
    if (catalogs) {
      dispatch(
        updateManyProductFilterOptions({
          filterId: "catalog",
          updates: catalogs.map((catalog) => ({
            id: catalog.id,
            changes: {
              selected: false,
            },
          })),
        })
      );
      resetOtherFilters();
    }
  }, [dispatch, catalogs, resetOtherFilters]);

  return (
    <Container ref={containerRef}>
      <Control onClick={() => setMenuIsOpen(!menuIsOpen)}>
        <h1>{currentCatalog?.title || DEFAULT_LABEL}</h1>
        <Indicator $menuIsOpen={menuIsOpen} />
      </Control>
      <Content>{children}</Content>
      <Menu ref={menuRef}>
        {catalogs
          ?.filter((catalog) => !catalog.isCurrent)
          ?.map((catalog) => (
            <MenuItem
              key={catalog.id}
              onClick={() => selectCatalog(catalog.id)}
            >
              {catalog.title}
            </MenuItem>
          ))}
        {currentCatalog && (
          <MenuItem onClick={() => selectNone()}>{DEFAULT_LABEL}</MenuItem>
        )}
      </Menu>
    </Container>
  );
};

export default Filter;
